import React from "react";
import { LegalPageLayout, P, Ul, Strong } from "../components/site/LegalPageLayout";
import Seo from "../components/site/Seo";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <P>
          Restaurant Photographer is a Melbourne-based hospitality photography studio
          operating at <a href="https://restaurantphotographer.com.au" className="text-ember underline-offset-4 hover:underline">restaurantphotographer.com.au</a>.
          We&apos;re committed to handling your personal information transparently and in
          accordance with the <Strong>Australian Privacy Principles (APPs)</Strong> under
          the <em>Privacy Act 1988</em> (Cth).
        </P>
        <P>
          This policy explains what personal information we collect, why we collect it,
          how it&apos;s used, and your rights regarding that information.
        </P>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "What we collect",
    body: (
      <>
        <P>When you contact us, request a quote or book a shoot, we collect:</P>
        <Ul items={[
          <><Strong>Identity &amp; contact</Strong> — your name, venue or brand name, email and phone number.</>,
          <><Strong>Project context</Strong> — Melbourne suburb, cuisine type, menu size, channels you intend to use the photography for, project timing and any free-text brief you submit.</>,
          <><Strong>Technical metadata</Strong> — the URL of the enquiry form you submitted from, the date and time of submission, and the A/B test variant of the call-to-action you were shown (used only to measure conversion effectiveness).</>,
        ]} />
        <P>
          We don&apos;t collect sensitive information (health, religion, political views,
          etc.) and we don&apos;t ask for it on any of our forms.
        </P>
      </>
    ),
  },
  {
    id: "why-we-collect",
    title: "Why we collect it",
    body: (
      <>
        <P>We collect personal information for clearly defined business reasons:</P>
        <Ul items={[
          "To respond to your enquiry by phone or email.",
          "To prepare and send a written quote.",
          "To plan and deliver the photography work you've booked.",
          "To invoice you and reconcile payments.",
          "To send transactional emails (e.g. quote confirmations, delivery notifications).",
          "To improve how our website converts enquiries — by analysing which call-to-action variants and form structures perform better.",
        ]} />
        <P>
          We do <Strong>not</Strong> use your information for unsolicited marketing,
          newsletters or third-party advertising lists.
        </P>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How we use your information",
    body: (
      <>
        <P>
          Your enquiry is stored in a secure database hosted on infrastructure managed
          by our platform provider. From there, your contact details are used by the
          photographer (or an authorised studio admin) to follow up directly — usually
          by phone or email within one business day.
        </P>
        <P>
          Project context (menu size, suburb, cuisine, channels) is used internally to
          scope the shoot accurately. None of this information is ever sold, leased or
          provided to advertising networks.
        </P>
      </>
    ),
  },
  {
    id: "who-we-share-with",
    title: "Who we share it with",
    body: (
      <>
        <P>
          We use a small set of trusted service providers to operate the studio. Each
          processes your information only to deliver a specific function:
        </P>
        <Ul items={[
          <><Strong>Resend</Strong> — to deliver transactional emails (your auto-acknowledgement and our internal lead notifications).</>,
          <><Strong>Our hosting platform</Strong> — to run this website and the database where your enquiry is stored.</>,
          <><Strong>Google Ads</Strong> — for conversion measurement of campaigns we run. We do not use Google Ads to retarget visitors or share email addresses.</>,
        ]} />
        <P>
          We do not share your information with any other third party except where
          required by Australian law, court order, or with your express consent (e.g.
          if you ask us to coordinate with your designer or agency).
        </P>
      </>
    ),
  },
  {
    id: "storage",
    title: "Data storage and security",
    body: (
      <>
        <P>
          Your enquiry data is stored in encrypted-at-rest databases managed by our
          platform provider. Access to the admin dashboard is protected by a
          cryptographically-random access token and is restricted to the studio owner.
        </P>
        <P>
          We retain enquiries indefinitely as part of our business records, except
          where you ask us to delete them (see <a href="#your-rights" className="text-ember underline-offset-4 hover:underline">Your rights</a> below).
          We never store payment-card details — invoicing is handled through external
          accounting and bank channels.
        </P>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and tracking",
    body: (
      <>
        <P>
          We use a small amount of browser <Strong>local storage</Strong> to remember
          which call-to-action variant you were shown (e.g. &quot;Get a Shoot Quote&quot;
          vs &quot;Estimate My Shoot&quot;) so we can measure which performs better.
          This is anonymous, contains no personal information, and is never linked back
          to your identity unless you submit a form.
        </P>
        <P>
          The site loads a Google Ads conversion tag for campaign measurement. This sets
          first- and third-party cookies that may help Google attribute a conversion to
          a paid ad click. You can opt out of Google&apos;s personalised advertising at
          <a href="https://adssettings.google.com" target="_blank" rel="noopener" className="text-ember underline-offset-4 hover:underline ml-1">adssettings.google.com</a>.
        </P>
        <P>
          We do not use any other analytics, tracking pixels, session-replay tools or
          social-media trackers on this site.
        </P>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <P>Under Australian privacy law you have the right to:</P>
        <Ul items={[
          "Request a copy of any personal information we hold about you.",
          "Ask us to correct information that is inaccurate or out of date.",
          "Ask us to delete your information from our records (subject to any legal retention obligations).",
          "Withdraw any consent you have previously given.",
          "Lodge a complaint with us, or with the Office of the Australian Information Commissioner (OAIC) if you believe we have mishandled your information.",
        ]} />
        <P>
          To exercise any of these rights, email <a href="mailto:hello@restaurantphotographer.com.au" className="text-ember underline-offset-4 hover:underline">hello@restaurantphotographer.com.au</a> with
          &quot;Privacy request&quot; in the subject line. We respond within 14 business days.
        </P>
      </>
    ),
  },
  {
    id: "children",
    title: "Children's privacy",
    body: (
      <P>
        Our services are intended for hospitality businesses, not individuals under 18.
        We do not knowingly collect personal information from minors. If you believe a
        minor has submitted information through our forms, contact us and we will
        delete it.
      </P>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <P>
        We may update this policy from time to time as our services evolve or as the
        law changes. When we do, we&apos;ll update the &quot;Last updated&quot; date at
        the top of this page. Material changes will be flagged at the top of the page
        for at least 30 days.
      </P>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <>
        <P>
          For any privacy-related question or request, contact:
        </P>
        <Ul items={[
          <>Email: <a href="mailto:hello@restaurantphotographer.com.au" className="text-ember underline-offset-4 hover:underline">hello@restaurantphotographer.com.au</a></>,
          <>Phone: <a href="tel:0494756111" className="text-ember underline-offset-4 hover:underline">0494 756 111</a></>,
          "Studio: Mobile — servicing Greater Melbourne, Victoria",
        ]} />
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <>
    <Seo />
    <LegalPageLayout
      breadcrumb="Privacy Policy"
      eyebrow="Privacy Policy · Australian Privacy Principles"
      title={<>Your information.<br /><span className="italic font-light text-ember">Handled honestly.</span></>}
      intro="A plain-English summary of what we collect, why we collect it, who we share it with, and the rights you have over your own information. Written to comply with the Australian Privacy Act 1988 and the Australian Privacy Principles."
      lastUpdated="15 February 2026"
      sections={SECTIONS}
    />
    </>
  );
}
